import React, { useEffect, useMemo, useState } from 'react';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import axios from 'axios';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormGrid,
  RequiredNote,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../../Components/Form';

const NEW_CLAUSE_SCHEMA = {
  clauseTitle: { label: 'Title', rules: [required()] },
};

const EMPTY_NEW_CLAUSE = { clauseTitle: '', ordreNumber: '' };

// axios interceptors may or may not have unwrapped the ApiResponse already
const unwrap = response => response?.data?.data ?? response?.data ?? response;

/**
 * Adds clause index rows to a procurement from the Requirement tab. The pick
 * list holds only clauses that are not active on this procurement: its
 * inactive clauses (activated again via onReactivated, saved with Save Draft)
 * and master Clause Index entries it does not have yet (persisted right away
 * by POST /procurement/{id}/clause-detail). The user can also create a new
 * clause index, which is saved to master data and added to this procurement.
 */
const AddClauseIndexModal = ({
  isOpen,
  toggle,
  procurementId,
  existingClauseDetailIds = [],
  inactiveClauses = [],
  onAdded,
  onReactivated,
}) => {
  const [mode, setMode] = useState('master');
  const [masterClauses, setMasterClauses] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [newClause, setNewClause] = useState(EMPTY_NEW_CLAUSE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const existingIds = useMemo(
    () => new Set(existingClauseDetailIds.filter(Boolean).map(String)),
    [existingClauseDetailIds]
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      setLoadingMaster(true);
      try {
        const response = await axios.get(
          `${api.API_URL}/clause-detail?PageNumber=1&PageSize=500&SortBy=OrdreNumber`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        );
        const data = unwrap(response);
        if (!cancelled) setMasterClauses(data?.items || []);
      } catch (error) {
        if (!cancelled) notify.apiError(error, 'load the clause index list');
      } finally {
        if (!cancelled) setLoadingMaster(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Keys are prefixed so a procurement clause id never collides with a
  // master clause id.
  const pickableClauses = useMemo(
    () =>
      [
        ...inactiveClauses.map(item => ({
          key: `procurement:${item.id}`,
          ordreNumber: item.ordreNumber,
          clauseTitle: item.clauseTitle,
          procurementClause: item,
        })),
        ...masterClauses
          .filter(clause => !existingIds.has(String(clause.id)))
          .map(clause => ({
            key: `master:${clause.id}`,
            ordreNumber: clause.ordreNumber,
            clauseTitle: clause.clauseTitle,
            clauseDetailId: String(clause.id),
          })),
      ].sort((a, b) => (a.ordreNumber ?? 0) - (b.ordreNumber ?? 0)),
    [inactiveClauses, masterClauses, existingIds]
  );

  const visibleClauses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pickableClauses;
    return pickableClauses.filter(
      clause =>
        clause.clauseTitle?.toLowerCase().includes(term) ||
        String(clause.ordreNumber ?? '').includes(term)
    );
  }, [pickableClauses, search]);

  const reset = () => {
    setMode('master');
    setSearch('');
    setSelectedIds([]);
    setNewClause(EMPTY_NEW_CLAUSE);
    setErrors({});
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    toggle();
  };

  const toggleSelected = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const postClause = body =>
    axios.post(
      `${api.API_URL}/procurement/${procurementId}/clause-detail`,
      body,
      {
        withXSRFToken: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

  const handleAddFromMaster = async () => {
    if (selectedIds.length === 0) {
      notify.warning('Select at least one clause index to add.');
      return;
    }
    const selected = pickableClauses.filter(clause =>
      selectedIds.includes(clause.key)
    );
    const reactivated = selected
      .filter(clause => clause.procurementClause)
      .map(clause => clause.procurementClause);
    const masterIds = selected
      .filter(clause => clause.clauseDetailId)
      .map(clause => clause.clauseDetailId);

    if (reactivated.length > 0) {
      onReactivated?.(reactivated);
      notify.success(
        `${
          reactivated.length === 1
            ? 'Clause activated.'
            : `${reactivated.length} clauses activated.`
        } Save the draft to keep the change.`
      );
    }
    if (masterIds.length === 0) {
      reset();
      toggle();
      return;
    }

    setSaving(true);
    const added = [];
    try {
      // one request per clause keeps the BE endpoint simple; lists are short
      for (const clauseDetailId of masterIds) {
        added.push(unwrap(await postClause({ clauseDetailId })));
      }
      notify.success(
        added.length === 1
          ? 'Clause index added.'
          : `${added.length} clause indexes added.`
      );
    } catch (error) {
      notify.apiError(error, 'add the clause index');
    } finally {
      setSaving(false);
      if (added.length > 0 || reactivated.length > 0) {
        if (added.length > 0) onAdded?.(added);
        reset();
        toggle();
      }
    }
  };

  const handleCreateNew = async () => {
    const nextErrors = validateForm(newClause, NEW_CLAUSE_SCHEMA);
    const order = String(newClause.ordreNumber ?? '').trim();
    if (order && !/^\d+$/.test(order)) {
      nextErrors.ordreNumber = 'Order number must be a whole number.';
    }
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, ['clauseTitle', 'ordreNumber']);
      return;
    }
    setSaving(true);
    try {
      const created = unwrap(
        await postClause({
          clauseTitle: newClause.clauseTitle.trim(),
          ordreNumber: order ? Number(order) : null,
        })
      );
      notify.success('Clause index created and added.');
      onAdded?.([created]);
      reset();
      toggle();
    } catch (error) {
      notify.apiError(error, 'create the clause index');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size='lg'>
      <ModalHeader toggle={handleClose} className='fw-semibold'>
        Add Clause Index
      </ModalHeader>
      <ModalBody>
        <div
          className='btn-group w-100 mb-3'
          role='group'
          aria-label='How to add the clause index'
        >
          <button
            type='button'
            className={`btn btn-sm ${
              mode === 'master' ? 'btn-primary' : 'btn-outline-primary'
            }`}
            onClick={() => setMode('master')}
            disabled={saving}
          >
            <i className='ri-list-check-2 me-1' aria-hidden='true' />
            Activate inactive clause
          </button>
          <button
            type='button'
            className={`btn btn-sm ${
              mode === 'new' ? 'btn-primary' : 'btn-outline-primary'
            }`}
            onClick={() => setMode('new')}
            disabled={saving}
          >
            <i className='ri-add-line me-1' aria-hidden='true' />
            Create new clause index
          </button>
        </div>

        {mode === 'master' ? (
          <>
            <Input
              type='search'
              bsSize='sm'
              className='mb-2'
              placeholder='Search inactive clause by title or order number'
              aria-label='Search clause index'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <p className='text-muted fs-12 mb-2'>
              {loadingMaster
                ? 'Loading clause index…'
                : `${pickableClauses.length} inactive ${
                    pickableClauses.length === 1 ? 'clause' : 'clauses'
                  } can be added back to this procurement.`}
            </p>
            <div className='pd-clause-pick'>
              {!loadingMaster && visibleClauses.length === 0 && (
                <div className='pd-empty'>
                  <span className='pd-empty__icon'>
                    <i className='ri-article-line' aria-hidden='true' />
                  </span>
                  <p>
                    {pickableClauses.length === 0
                      ? 'Every clause is already active. Create a new clause index instead.'
                      : 'No inactive clause matches the search.'}
                  </p>
                </div>
              )}
              {visibleClauses.map(clause => (
                <label key={clause.key} className='pd-clause-pick__row'>
                  <Input
                    type='checkbox'
                    className='form-check-input mt-0'
                    checked={selectedIds.includes(clause.key)}
                    disabled={saving}
                    onChange={() => toggleSelected(clause.key)}
                  />
                  <span className='pd-clause-pick__order'>
                    {clause.ordreNumber ?? '-'}
                  </span>
                  <span className='flex-grow-1'>{clause.clauseTitle}</span>
                  <span className='badge bg-secondary-subtle text-secondary'>
                    Inactive
                  </span>
                </label>
              ))}
            </div>
          </>
        ) : (
          <>
            <RequiredNote />
            <FormGrid columns={2}>
              <TextField
                label='Title'
                name='clauseTitle'
                required
                placeholder='e.g. Force Majeure'
                value={newClause.clauseTitle}
                error={errors.clauseTitle}
                onChange={e => {
                  setNewClause(prev => ({
                    ...prev,
                    clauseTitle: e.target.value,
                  }));
                  setErrors(prev => ({ ...prev, clauseTitle: '' }));
                }}
              />
              <TextField
                label='Order number'
                name='ordreNumber'
                type='number'
                placeholder='Auto (after the last clause)'
                value={newClause.ordreNumber}
                error={errors.ordreNumber}
                onChange={e => {
                  setNewClause(prev => ({
                    ...prev,
                    ordreNumber: e.target.value,
                  }));
                  setErrors(prev => ({ ...prev, ordreNumber: '' }));
                }}
              />
            </FormGrid>
            <p className='text-muted fs-12 mb-0 mt-2'>
              <i className='ri-information-line me-1' aria-hidden='true' />
              The clause index is saved to master data (active) and added to
              this procurement. Procurements that already exist are not changed;
              new procurements will include it.
            </p>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-close-line'
          disabled={saving}
          onClick={handleClose}
        >
          Close
        </ActionButton>
        {mode === 'master' ? (
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-add-line'
            loading={saving}
            disabled={selectedIds.length === 0}
            onClick={handleAddFromMaster}
          >
            {selectedIds.length > 1
              ? `Add ${selectedIds.length} clauses`
              : 'Add clause'}
          </ActionButton>
        ) : (
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-save-line'
            loading={saving}
            onClick={handleCreateNew}
          >
            Create &amp; add
          </ActionButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AddClauseIndexModal;
